import { useEffect, useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { Consent } from '../constants';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface ConsentPreferences {
  analytics_storage: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
  timestamp: number;
}

export const GoogleCMP = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState<'granted' | 'denied'>('denied');
  const [adConsent, setAdConsent] = useState<'granted' | 'denied'>('denied');

  useEffect(() => {
    // Check if consent has been given
    const storedConsent = localStorage.getItem(Consent.StorageKey);
    
    if (storedConsent) {
      try {
        const consent: ConsentPreferences = JSON.parse(storedConsent);
        const expiryTime = consent.timestamp + (Consent.ExpiryDays * 24 * 60 * 60 * 1000);
        
        if (Date.now() < expiryTime) {
          // Consent is still valid
          updateConsent(consent.analytics_storage, consent.ad_storage);
          return;
        }
      } catch (e) {
        console.error('Error parsing stored consent:', e);
      }
    }
    
    // Show banner if no valid consent
    setShowBanner(true);
    setAnalyticsConsent('denied');
    setAdConsent('denied');
  }, []);

  const updateConsent = (analytics: 'granted' | 'denied', ad: 'granted' | 'denied') => {
    // Update Google Consent Mode
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': analytics,
        'ad_storage': ad,
      });
    }

    // Store consent preferences
    const consent: ConsentPreferences = {
      analytics_storage: analytics,
      ad_storage: ad,
      timestamp: Date.now(),
    };
    localStorage.setItem(Consent.StorageKey, JSON.stringify(consent));
  };

  const handleAcceptAll = () => {
    updateConsent('granted', 'granted');
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    updateConsent('denied', 'denied');
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    updateConsent(analyticsConsent, adConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleShowSettings = () => {
    setShowSettings(true);
    setShowBanner(false);
  };

  if (!showBanner && !showSettings) {
    return null;
  }

  return (
    <>
      {/* Consent Banner */}
      {showBanner && (
        <div 
          className="position-fixed bottom-0 start-0 end-0 bg-dark text-light p-4 shadow-lg"
          style={{ zIndex: 9999 }}
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8 mb-3 mb-md-0">
                <h5 className="mb-2">Cookie Consent</h5>
                <p className="mb-0 small">
                  We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                  By clicking "Accept All", you consent to our use of cookies. You can also manage your preferences.
                </p>
              </div>
              <div className="col-md-4 text-md-end">
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  className="me-2 mb-2 mb-md-0"
                  onClick={handleShowSettings}
                >
                  Settings
                </Button>
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  className="me-2 mb-2 mb-md-0"
                  onClick={handleRejectAll}
                >
                  Reject All
                </Button>
                <Button 
                  variant="light" 
                  size="sm"
                  onClick={handleAcceptAll}
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <Modal 
        show={showSettings} 
        onHide={() => setShowSettings(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cookie Preferences</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-4">
            Manage your cookie preferences. You can enable or disable different types of cookies below.
          </p>
          
          <Form>
            <Form.Check
              type="switch"
              id="analytics-consent"
              label="Analytics Cookies"
              checked={analyticsConsent === 'granted'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAnalyticsConsent(e.target.checked ? 'granted' : 'denied')}
              className="mb-3"
            />
            <Form.Text className="text-muted d-block mb-3">
              These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
            </Form.Text>

            <Form.Check
              type="switch"
              id="ad-consent"
              label="Advertising Cookies"
              checked={adConsent === 'granted'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdConsent(e.target.checked ? 'granted' : 'denied')}
              className="mb-3"
            />
            <Form.Text className="text-muted d-block mb-3">
              These cookies are used to make advertising messages more relevant to you and your interests.
            </Form.Text>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSettings(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveSettings}>
            Save Preferences
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

